import React, { useState, useEffect, useCallback } from 'react';
import { Sliders, RefreshCw, Save, CheckCircle, Loader2 } from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import ErrorAlert from '@framework/components/ErrorAlert';
import ActionButton from '@framework/components/ActionButton';

const TIERS = ['free', 'plus', 'pro'];

const TIER_COLORS = {
  free: 'text-gray-600 dark:text-gray-300',
  plus: 'text-blue-600 dark:text-blue-400',
  pro: 'text-orange-600 dark:text-orange-400',
};

const TIER_BADGE = {
  free: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  plus: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  pro: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
};

// Field definitions grouped by section.
// type: 'number' | 'bool'
const SECTIONS = [
  {
    label: 'Chat',
    fields: [
      { key: 'daily_chat_sessions', label: 'Daily chat sessions', type: 'number', hint: 'How many times a user can open chat per day' },
      { key: 'max_chat_turns', label: 'Max turns per session', type: 'number', hint: 'Messages per session before the hard stop' },
      { key: 'daily_from_chat', label: 'Daily recipes from chat', type: 'number', hint: 'How many recipes can be generated via chat per day' },
    ],
  },
  {
    label: 'Recipe imports',
    fields: [
      { key: 'daily_scan_recipe', label: 'Scan (camera)', type: 'number' },
      { key: 'daily_from_url', label: 'From URL', type: 'number' },
      { key: 'daily_from_photo', label: 'From photo', type: 'number' },
      { key: 'daily_from_video', label: 'From video', type: 'number' },
      { key: 'daily_combined_imports', label: 'Combined daily imports', type: 'number', hint: 'Shared cap across all import types' },
    ],
  },
  {
    label: 'Storage',
    fields: [
      { key: 'max_recipes', label: 'Max saved recipes', type: 'number' },
      { key: 'max_pantry_items', label: 'Max pantry items', type: 'number' },
      { key: 'max_shopping_lists', label: 'Max shopping lists', type: 'number' },
    ],
  },
  {
    label: 'AI features',
    fields: [
      { key: 'monthly_image_generations', label: 'Monthly AI image generations', type: 'number' },
      { key: 'can_generate_ai_info', label: 'Can generate AI info (Encyclopedia)', type: 'bool' },
    ],
  },
];

function TierCell({ value, fieldType, onChange }) {
  if (fieldType === 'bool') {
    return (
      <div className="flex justify-center">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
        />
      </div>
    );
  }
  return (
    <input
      type="number"
      min="0"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
      className="w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white text-right focus:ring-2 focus:ring-orange-500 focus:border-transparent"
    />
  );
}

export default function TierLimitsPage() {
  const { apiService: api } = useTheme();
  const [limits, setLimits] = useState(null);   // { free: {...}, plus: {...}, pro: {...} }
  const [dirty, setDirty] = useState({});        // { free: {...changed fields...} }
  const [saving, setSaving] = useState({});      // { free: true/false }
  const [saved, setSaved] = useState({});        // { free: true } — briefly shown after save
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTierLimits();
      setLimits(data);
      setDirty({});
      setSaveError({});
    } catch (err) {
      setError('Failed to load tier limits');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const getValue = (tier, key) => {
    if (dirty[tier] && key in dirty[tier]) return dirty[tier][key];
    return limits?.[tier]?.[key];
  };

  const handleChange = (tier, key, value) => {
    setDirty((prev) => ({
      ...prev,
      [tier]: { ...(prev[tier] || {}), [key]: value },
    }));
    setSaved((prev) => ({ ...prev, [tier]: false }));
  };

  const handleSave = async (tier) => {
    const changes = dirty[tier];
    if (!changes || Object.keys(changes).length === 0) return;

    setSaving((prev) => ({ ...prev, [tier]: true }));
    setSaveError((prev) => ({ ...prev, [tier]: null }));

    try {
      const updated = await api.updateTierLimit(tier, changes);
      setLimits((prev) => ({ ...prev, [tier]: updated }));
      setDirty((prev) => { const n = { ...prev }; delete n[tier]; return n; });
      setSaved((prev) => ({ ...prev, [tier]: true }));
      setTimeout(() => setSaved((prev) => ({ ...prev, [tier]: false })), 2000);
    } catch (err) {
      setSaveError((prev) => ({ ...prev, [tier]: err?.message || 'Save failed' }));
    } finally {
      setSaving((prev) => ({ ...prev, [tier]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Sliders className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tier Limits</h1>
        </div>
        <ActionButton icon={RefreshCw} variant="ghost" size="sm" onClick={load}>Refresh</ActionButton>
      </div>

      <ErrorAlert error={error} onClose={() => setError(null)} />

      {limits && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/2">
                  Limit
                </th>
                {TIERS.map((tier) => (
                  <th key={tier} className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${TIER_BADGE[tier]}`}>
                        {tier}
                      </span>
                      {dirty[tier] && Object.keys(dirty[tier]).length > 0 ? (
                        <button
                          onClick={() => handleSave(tier)}
                          disabled={saving[tier]}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 transition-colors"
                        >
                          {saving[tier] ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Save className="w-3 h-3" />
                          )}
                          Save
                        </button>
                      ) : saved[tier] ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                          <CheckCircle className="w-3 h-3" /> Saved
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                      )}
                      {saveError[tier] && (
                        <span className="text-xs text-red-500">{saveError[tier]}</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {SECTIONS.map((section) => (
                <React.Fragment key={section.label}>
                  <tr className="bg-gray-50 dark:bg-gray-900/50">
                    <td colSpan={4} className="px-5 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {section.label}
                    </td>
                  </tr>
                  {section.fields.map((field) => (
                    <tr key={field.key} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <td className="px-5 py-2.5">
                        <div className="text-sm text-gray-800 dark:text-gray-200">{field.label}</div>
                        {field.hint && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">{field.hint}</div>
                        )}
                      </td>
                      {TIERS.map((tier) => (
                        <td key={tier} className="px-4 py-2.5 text-center">
                          <TierCell
                            value={getValue(tier, field.key)}
                            fieldType={field.type}
                            onChange={(val) => handleChange(tier, field.key, val)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
